/**
 * Adaptadores NCS/EMG → formato de PatternCrossValidationService.
 * Usado por CompleteClinicalWorkflow y ResultAnalyzer (paridad DRY).
 */
import type { EMGNerveRecord } from '../components/EMGNeedleAnalysis';
import type { ClinicalReport, EMGMuscleResult } from './jsonReportGenerator';
import type { NCSTestResult } from '../types/ncs';
import type { UnifiedEMGResults } from '../types/emg';
import {
  CrossValidationResult,
  PatternCrossValidationService,
} from './patternCrossValidationService';

export interface CrossValidationNcsSnapshot {
  motor?: { amplitude?: number; velocity?: number; latency?: number };
  sensory?: { amplitude?: number; velocity?: number };
}

export interface CrossValidationEmgSnapshot {
  spontaneousActivity?: {
    fibrillations?: boolean;
    positiveWaves?: boolean;
    fasciculations?: boolean;
  };
  motorUnitPotentials?: { duration?: number; amplitude?: number; polyphasia?: number };
  recruitmentPattern?: string;
}

export function mapRecruitmentForCrossValidation(pattern: string | undefined): string {
  switch (pattern) {
    case 'reduced_incomplete':
    case 'reduced_complete':
    case 'reducido':
      return 'reduced';
    case 'precoz':
      return 'early';
    case 'discrete':
      return 'discrete';
    default:
      return pattern || 'normal';
  }
}

export function buildNcsSnapshotFromResults(results: NCSTestResult[]): CrossValidationNcsSnapshot {
  const motor = results.filter((r) => r.type === 'motor');
  const sensory = results.filter((r) => r.type === 'sensory');
  const snapshot: CrossValidationNcsSnapshot = {};

  if (motor.length > 0) {
    const velocities = motor.map((r) => r.velocity).filter((v) => v > 0);
    snapshot.motor = {
      amplitude: Math.min(...motor.map((r) => r.amplitude)),
      latency: Math.max(...motor.map((r) => r.latency)),
      velocity: velocities.length ? Math.min(...velocities) : undefined,
    };
  }

  if (sensory.length > 0) {
    const velocities = sensory.map((r) => r.velocity).filter((v) => v > 0);
    snapshot.sensory = {
      amplitude: Math.min(...sensory.map((r) => r.amplitude)),
      velocity: velocities.length ? Math.min(...velocities) : undefined,
    };
  }

  return snapshot;
}

function emgRecordAbnormalityScore(record: EMGNerveRecord): number {
  let score = 0;
  if (record.insertionalActivity === 'increased' || record.insertionalActivity === 'decreased') score += 1;
  if (record.insertionalActivity === 'absent') score += 2;
  const fib = record.spontaneousActivity.fibrillations;
  const pos = record.spontaneousActivity.positiveWaves;
  if (fib === true || (typeof fib === 'number' && fib > 0)) score += 2;
  if (pos === true || (typeof pos === 'number' && pos > 0)) score += 2;
  if (record.spontaneousActivity.fasciculations) score += 1;
  if (record.motorUnitPotentials.duration > 15 || record.motorUnitPotentials.duration < 5) score += 1;
  if (record.motorUnitPotentials.amplitude > 5000 || record.motorUnitPotentials.amplitude < 100) score += 1;
  if (record.motorUnitPotentials.polyphasia > 20) score += 1;
  if (record.recruitmentPattern && record.recruitmentPattern !== 'normal') score += 1;
  return score;
}

export function buildEmgSnapshotFromNerveRecords(
  records: EMGNerveRecord[]
): CrossValidationEmgSnapshot | null {
  if (!records.length) return null;

  const target = records.reduce((best, record) =>
    emgRecordAbnormalityScore(record) > emgRecordAbnormalityScore(best) ? record : best
  );

  const fib = target.spontaneousActivity.fibrillations;
  const pos = target.spontaneousActivity.positiveWaves;

  return {
    spontaneousActivity: {
      fibrillations: fib === true || (typeof fib === 'number' && fib > 0),
      positiveWaves: pos === true || (typeof pos === 'number' && pos > 0),
      fasciculations: target.spontaneousActivity.fasciculations,
    },
    motorUnitPotentials: {
      amplitude: target.motorUnitPotentials.amplitude,
      duration: target.motorUnitPotentials.duration,
      polyphasia: target.motorUnitPotentials.polyphasia,
    },
    recruitmentPattern: mapRecruitmentForCrossValidation(target.recruitmentPattern),
  };
}

function parseSpontaneousSummary(summary: string): CrossValidationEmgSnapshot['spontaneousActivity'] {
  const lower = summary.toLowerCase();
  if (!lower || lower === 'ausente' || lower === 'normal') {
    return { fibrillations: false, positiveWaves: false, fasciculations: false };
  }
  return {
    fibrillations: lower.includes('fibril'),
    positiveWaves: lower.includes('positiva') || lower.includes('positive'),
    fasciculations: lower.includes('fascicul'),
  };
}

export function buildEmgSnapshotFromMuscles(
  muscles: EMGMuscleResult[]
): CrossValidationEmgSnapshot | null {
  if (!muscles.length) return null;

  const target = muscles.reduce((best, muscle) =>
    muscle.abnormalityScore > best.abnormalityScore ? muscle : best
  );

  return {
    spontaneousActivity: parseSpontaneousSummary(target.spontaneousActivitySummary),
    motorUnitPotentials: {
      amplitude: target.mupAmplitude,
      duration: target.mupDuration,
      polyphasia: target.mupPolyphasia,
    },
    recruitmentPattern: mapRecruitmentForCrossValidation(target.recruitmentPattern),
  };
}

export function buildEmgSnapshotFromUnified(
  emgData: UnifiedEMGResults
): CrossValidationEmgSnapshot {
  const data = emgData.spontaneousActivityData;
  let spontaneous: CrossValidationEmgSnapshot['spontaneousActivity'];

  if (data) {
    const fib = data.fibrillations;
    const pos = data.positiveSharpWaves;
    spontaneous = {
      fibrillations: typeof fib === 'number' ? fib > 0 : !!fib,
      positiveWaves: typeof pos === 'number' ? pos > 0 : !!pos,
      fasciculations: data.fasciculations?.present ?? false,
    };
  } else {
    spontaneous = parseSpontaneousSummary(emgData.spontaneousActivity);
  }

  return {
    spontaneousActivity: spontaneous,
    motorUnitPotentials: {
      amplitude: emgData.motorUnitActionPotentials.amplitude,
      duration: emgData.motorUnitActionPotentials.duration,
      polyphasia: emgData.motorUnitActionPotentials.polyphasia,
    },
    recruitmentPattern: mapRecruitmentForCrossValidation(emgData.recruitment),
  };
}

export function resolvePatientAgeForCrossValidation(
  explicitAge?: number,
  dateOfBirth?: string
): number | undefined {
  if (explicitAge !== undefined && explicitAge > 0) return explicitAge;
  if (!dateOfBirth) return undefined;
  const birth = new Date(dateOfBirth);
  if (Number.isNaN(birth.getTime())) return undefined;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) age -= 1;
  return age > 0 ? age : undefined;
}

export function validateClinicalReportCrossConsistency(
  report: ClinicalReport,
  patientAge?: number
): CrossValidationResult | null {
  const emgBlock = report.electrophysiologicalData.emg;
  if (!emgBlock || emgBlock.status !== 'performed') return null;

  const emgSnapshot =
    emgBlock.records && emgBlock.records.length > 0
      ? buildEmgSnapshotFromNerveRecords(emgBlock.records)
      : buildEmgSnapshotFromMuscles(emgBlock.muscles ?? []);

  if (!emgSnapshot) return null;

  const ncsResults = report.electrophysiologicalData.ncs?.results ?? [];
  const ncsSnapshot = buildNcsSnapshotFromResults(ncsResults);
  const age =
    patientAge ??
    report.patient.demographics.age ??
    undefined;

  return PatternCrossValidationService.validatePatternConsistency(
    ncsSnapshot,
    emgSnapshot,
    age
  );
}

export function validateStudyCrossConsistency(
  ncsResults: NCSTestResult[],
  emgData: UnifiedEMGResults,
  patientAge?: number
): CrossValidationResult {
  return PatternCrossValidationService.validatePatternConsistency(
    buildNcsSnapshotFromResults(ncsResults),
    buildEmgSnapshotFromUnified(emgData),
    patientAge
  );
}
